/**
 * Chat Window Component
 * Main container for chat interface
 *
 * Accessibility:
 * - ARIA live region for message updates
 * - Safe area handling for mobile devices
 * - Respects reduced motion preferences
 * - Proper dialog role and labeling
 * - Keyboard navigation (Escape to close)
 */

import React, { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { Variants } from 'motion/react';
import type { ChatMessage, ChatAuthState, RateLimit, ChatSuggestion } from '../types/chat.types';
import { ChatHeader } from './ChatHeader';
import { ChatHeaderBanner } from './ChatHeaderBanner';
import { ChatMessage as ChatMessageComponent } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { ChatAuthGate } from './ChatAuthGate';
import { OfflineIndicator } from './OfflineIndicator';
import { ConversationSkeleton } from './ChatSkeletons';
import { AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

/**
 * Animation variants following DonationDrawer pattern
 * Spring physics for natural, enterprise-grade motion
 */
const desktopDrawerVariants: Variants = {
  hidden: { x: '100%', opacity: 0.9 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 30,
      stiffness: 300,
      mass: 0.8,
    },
  },
  exit: {
    x: '100%',
    opacity: 0.9,
    transition: { duration: 0.2, ease: 'easeIn' },
  },
};

interface ChatWindowProps {
  messages: ChatMessage[];
  onClose: () => void;
  onToggleHistory: () => void;
  onSendMessage: (message: string) => Promise<void>;
  onNewChat?: () => void;
  onGuestContinue?: () => void;
  isOpen: boolean;
  isSending?: boolean;
  isStreaming?: boolean;
  /** Show typing dots only while awaiting first chunk; hide when streaming message visible */
  showTypingIndicator?: boolean;
  isLoadingConversation?: boolean;
  authState?: ChatAuthState;
  chatbotName?: string;
  rateLimit?: RateLimit | null;
  queuedMessagesCount?: number;
  rateLimitRetryAfter?: number | null;
  handleSuggestionClick?: (suggestion: ChatSuggestion) => Promise<void>;
  getSuggestionsForMessage?: (messageId: string) => { suggestions: ChatSuggestion[]; isOffTopic: boolean } | null;
  // Message action handlers
  onFeedback?: (messageId: string, isPositive: boolean) => void;
  onRegenerate?: (messageId: string) => void;
  canRegenerateMessage?: (messageId: string) => boolean; // Check if regeneration is possible
  getMessageFeedback?: (messageId: string) => 'positive' | 'negative' | null;
  className?: string;
}

/**
 * Chat window container component
 * Responsive: full screen on mobile, fixed size on desktop
 */
export const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  onClose,
  onToggleHistory,
  onSendMessage,
  onNewChat,
  onGuestContinue,
  isOpen,
  isSending = false,
  isStreaming = false,
  showTypingIndicator = false,
  handleSuggestionClick,
  getSuggestionsForMessage,
  isLoadingConversation = false,
  authState = 'checking',
  chatbotName = 'Ask Hajra',
  rateLimit = null,
  queuedMessagesCount = 0,
  rateLimitRetryAfter = null,
  // Message action handlers
  onFeedback,
  onRegenerate,
  canRegenerateMessage,
  getMessageFeedback,
  className
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const lastMessageCountRef = useRef(0);
  const lastStreamingContentLengthRef = useRef<number>(0);
  const streamingMessageIdRef = useRef<string | null>(null);
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const { prefersReducedMotion } = useReducedMotion();

  /**
   * Check if user is near the bottom of the messages container
   */
  const isNearBottom = useCallback((): boolean => {
    if (!messagesContainerRef.current) return true;

    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    return distanceFromBottom < 100;
  }, []);

  /**
   * Smooth scroll to bottom
   */
  const scrollToBottom = useCallback((force = false): void => {
    if (!messagesEndRef.current || !isOpen || !messagesContainerRef.current) return;

    if (scrollAnimationFrameRef.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }

    if (!force && isUserScrollingRef.current) {
      return;
    }

    const container = messagesContainerRef.current;

    if (isStreaming) {
      scrollAnimationFrameRef.current = requestAnimationFrame(() => {
        if (container && shouldAutoScrollRef.current) {
          const targetScroll = container.scrollHeight - container.clientHeight;
          container.scrollTop = targetScroll;
          scrollAnimationFrameRef.current = null;
        }
      });
      return;
    }

    messagesEndRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'end',
      inline: 'nearest'
    });
  }, [isOpen, isStreaming, prefersReducedMotion]);

  /**
   * Optimized scroll handler
   */
  useLayoutEffect(() => {
    if (!isOpen) return;

    const streamingMessage = messages.find(m => m.isStreaming);
    const currentStreamingId = streamingMessage?.id || null;
    const currentContentLength = streamingMessage?.content?.length || 0;

    const contentChanged =
      currentStreamingId !== streamingMessageIdRef.current ||
      (currentStreamingId && currentContentLength !== lastStreamingContentLengthRef.current);

    if (currentStreamingId) {
      streamingMessageIdRef.current = currentStreamingId;
      lastStreamingContentLengthRef.current = currentContentLength;
    }

    const messageCount = messages.length;
    const isNewMessage = messageCount > lastMessageCountRef.current;
    lastMessageCountRef.current = messageCount;

    const lastMessage = messages[messages.length - 1];
    const isUserMessage = lastMessage?.type === 'user';

    if (isStreaming && shouldAutoScrollRef.current) {
      if (contentChanged || isNewMessage) {
        scrollToBottom(true);
      }
      return;
    }

    if (isNewMessage && !isStreaming) {
      if (isUserMessage) {
        shouldAutoScrollRef.current = true;
        scrollToBottom(true);
        return;
      }

      const nearBottom = isNearBottom();
      shouldAutoScrollRef.current = nearBottom;

      if (nearBottom) {
        scrollToBottom(false);
      }
    }
  }, [messages, isStreaming, isOpen, isNearBottom, scrollToBottom]);

  /**
   * Reset tracking refs when streaming stops
   */
  useEffect(() => {
    if (!isStreaming) {
      lastStreamingContentLengthRef.current = 0;
      streamingMessageIdRef.current = null;
    }
  }, [isStreaming]);

  /**
   * Track user scroll with IntersectionObserver
   */
  useEffect(() => {
    if (!isOpen || !messagesEndRef.current || !messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const endElement = messagesEndRef.current;

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        shouldAutoScrollRef.current = entry.isIntersecting || entry.intersectionRatio > 0.1;
      },
      {
        root: container,
        rootMargin: '100px 0px',
        threshold: [0, 0.1, 0.5, 1]
      }
    );

    intersectionObserverRef.current.observe(endElement);

    let scrollTimeout: number | null = null;
    const handleScroll = (): void => {
      isUserScrollingRef.current = true;

      if (scrollTimeout !== null) {
        window.clearTimeout(scrollTimeout);
      }

      scrollTimeout = window.setTimeout(() => {
        isUserScrollingRef.current = false;
        scrollTimeout = null;
      }, 300);

      shouldAutoScrollRef.current = isNearBottom();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    const currentObserver = intersectionObserverRef.current;
    const currentScrollTimeout = scrollTimeout;
    const currentAnimationFrame = scrollAnimationFrameRef.current;

    return (): void => {
      container.removeEventListener('scroll', handleScroll);
      if (currentObserver) {
        currentObserver.disconnect();
        intersectionObserverRef.current = null;
      }
      if (currentScrollTimeout !== null) {
        window.clearTimeout(currentScrollTimeout);
      }
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (currentAnimationFrame !== null) {
        cancelAnimationFrame(currentAnimationFrame);
      }
      if (scrollAnimationFrameRef.current !== null) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
        scrollAnimationFrameRef.current = null;
      }
    };
  }, [isOpen, isNearBottom]);

  /**
   * Handle ESC key to close chat
   */
  useEffect((): (() => void) | undefined => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  /**
   * Scroll to bottom when chat opens
   */
  useEffect((): (() => void) | undefined => {
    if (!isOpen) return;

    if (messagesContainerRef.current) {
      const timeout = setTimeout(() => {
        if (messagesContainerRef.current && messagesEndRef.current) {
          shouldAutoScrollRef.current = true;
          scrollToBottom(true);
        }
      }, 150);

      return (): void => {
        clearTimeout(timeout);
      };
    }

    return undefined;
  }, [isOpen, scrollToBottom]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <>
        {/* Backdrop - darker with blur for focus direction */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:block hidden"
          aria-hidden="true"
        />

        {/* Chat Window - Full-height sidebar drawer */}
        <motion.div
          variants={prefersReducedMotion ? undefined : desktopDrawerVariants}
          initial={prefersReducedMotion ? { opacity: 0 } : 'hidden'}
          animate={prefersReducedMotion ? { opacity: 1 } : 'visible'}
          exit={prefersReducedMotion ? { opacity: 0 } : 'exit'}
          className={cn(
            // Desktop: Full-height right sidebar
            'fixed right-0 top-0 bottom-0 z-50',
            'w-[480px] max-w-[90vw]',
            'h-screen h-dvh',

            // Appearance - rounded left edge only (flush to right)
            'bg-white rounded-l-2xl',
            'shadow-2xl border-l border-gray-200/60',
            'flex flex-col overflow-hidden',

            // Mobile: full screen (unchanged behavior)
            'max-md:inset-0 max-md:rounded-none',
            'max-md:w-full max-md:max-w-full',
            'max-md:border-0',

            className
          )}
          role="dialog"
          aria-modal="true"
          aria-label={`Chat with ${chatbotName}`}
        >
          {/* Header */}
          <div className="shrink-0 rounded-tl-2xl max-md:rounded-none">
            <ChatHeaderBanner />
            <ChatHeader
              onClose={onClose}
              onToggleHistory={onToggleHistory}
              onNewChat={onNewChat}
              chatbotName={chatbotName}
              isAuthenticated={authState === 'authenticated'}
            />
          </div>

          {/* Offline Indicator */}
          <OfflineIndicator />

          {/* Rate Limit Warning Banner */}
          {rateLimit && rateLimit.remaining < 5 && rateLimit.remaining > 0 && (
            <div
              className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2.5"
              role="status"
            >
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" aria-hidden="true" />
              <span className="text-sm text-amber-800">
                {rateLimit.remaining} {rateLimit.remaining === 1 ? 'message' : 'messages'} remaining today
              </span>
            </div>
          )}

          {/* Queued Messages Banner */}
          {queuedMessagesCount > 0 && (
            <div
              className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center gap-2.5"
              role="status"
            >
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0" aria-hidden="true" />
              <span className="text-sm text-blue-800">
                {queuedMessagesCount} {queuedMessagesCount === 1 ? 'message' : 'messages'} queued
                {rateLimitRetryAfter !== null && rateLimitRetryAfter > 0 && (
                  <span className="ml-1.5 font-medium">, sending in {rateLimitRetryAfter}s</span>
                )}
              </span>
            </div>
          )}

          {/* Messages Area */}
          <div
            ref={messagesContainerRef}
            className={cn(
              'flex-1 overflow-y-auto overflow-x-hidden',
              'px-4 py-5',
              'bg-linear-to-b from-gray-50/50 to-white'
            )}
            role="log"
            aria-live="polite"
            aria-label="Chat messages"
          >
            {/* Loading conversation state */}
            {isLoadingConversation ? (
              <ConversationSkeleton />
            ) : authState === 'checking' ? (
              /* Checking authentication */
              <div className="flex items-center justify-center h-full" role="status">
                <div className="text-center px-6">
                  <Loader2 className="w-8 h-8 text-emerald-600 mx-auto mb-3 animate-spin" aria-hidden="true" />
                  <p className="text-sm text-gray-500">Checking authentication...</p>
                </div>
              </div>
            ) : authState === 'prompt' ? (
              /* Auth gate */
              <ChatAuthGate
                onGuestContinue={onGuestContinue || ((): void => {})}
                onNavigate={onClose}
              />
            ) : messages.length === 0 ? (
              /* Empty state with suggestions */
              <div className="flex flex-col items-center justify-center h-full">
                <div className="text-center px-6 max-w-sm">
                  <p className="text-gray-700 mb-2 font-medium text-lg">
                    Chat with {chatbotName}
                  </p>
                  <p className="text-base text-gray-500 leading-relaxed mb-6">
                    Ask me anything about campaigns, donations, or Islamic charity
                  </p>

                  {/* Suggested Questions */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
                      Try asking:
                    </p>
                    <div className="flex flex-col gap-2" role="list" aria-label="Suggested questions">
                      {[
                        'How do I start a campaign?',
                        'What is Zakat and how do I calculate it?',
                        'How can I make a donation?'
                      ].map((question, idx) => (
                        <button
                          key={idx}
                          onClick={() => onSendMessage(question)}
                          className={cn(
                            'text-left px-4 py-3 rounded-xl',
                            'min-h-[48px]', // Touch target
                            'bg-gray-50 hover:bg-gray-100',
                            'border border-gray-200',
                            'text-base text-gray-700',
                            'transition-colors duration-150',
                            'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/50'
                          )}
                          type="button"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Messages list */
              <>
                {messages.map((message: ChatMessage) => {
                  const suggestionData = getSuggestionsForMessage?.(message.id);
                  const feedbackGiven = getMessageFeedback?.(message.id) ?? null;
                  // Only pass onRegenerate if regeneration is possible for this message
                  const canRegenerate = canRegenerateMessage?.(message.id) ?? false;
                  return (
                    <ChatMessageComponent
                      key={message.id}
                      message={message}
                      suggestions={suggestionData?.suggestions}
                      isOffTopic={suggestionData?.isOffTopic}
                      onSuggestionClick={handleSuggestionClick}
                      onFeedback={onFeedback}
                      onRegenerate={canRegenerate ? onRegenerate : undefined}
                      feedbackGiven={feedbackGiven}
                    />
                  );
                })}

                {/* Typing indicator - only while awaiting first chunk */}
                {showTypingIndicator && <TypingIndicator chatbotName={chatbotName} />}

                {/* Scroll anchor */}
                <div ref={messagesEndRef} aria-hidden="true" />
              </>
            )}
          </div>

          {/* Input Area */}
          {(authState === 'authenticated' || authState === 'guest') && (
            <div
              className={cn(
                'shrink-0',
                // Safe area for devices with home indicator
                'max-md:pb-[env(safe-area-inset-bottom,0px)]'
              )}
            >
              <ChatInput
                onSend={onSendMessage}
                disabled={isSending || (rateLimit !== null && rateLimit.remaining === 0)}
                isStreaming={isStreaming}
                placeholder={`Message ${chatbotName.replace(/^Ask\s+/, '')}...`}
                rateLimit={rateLimit}
                rateLimitRetryAfter={rateLimitRetryAfter}
              />
            </div>
          )}
        </motion.div>
      </>
    </AnimatePresence>
  );
};

export default ChatWindow;
